import { encodeAbiParameters, encodeFunctionData, keccak256, toBytes, toHex } from "viem";
import { botnetRegistryV2Abi } from "../../../shared/src/abi-v2";
import { encodeV2Utf8Prompt } from "./v2-prompt-codec";

const ADDRESS = /^0x[0-9a-f]{40}$/;
const HASH = /^0x[0-9a-f]{64}$/;
const SIGNATURE = /^0x[0-9a-f]{130}$/;
const MAX_UINT64 = (1n << 64n) - 1n;
const MAX_SAFE_BLOCK = BigInt(Number.MAX_SAFE_INTEGER);
const MAX_UINT256 = (1n << 256n) - 1n;
const PROMPT_SEQUENCE_TYPEHASH = keccak256(toBytes("BotnetRegistryV2PromptSequence(uint8 encoding,uint32 count)"));
const ZERO32 = `0x${"00".repeat(32)}`;

type Row = Record<string, unknown>;
function isRecord(value: unknown): value is Row { return value !== null && typeof value === "object" && !Array.isArray(value); }
function exactKeys(value: Row, keys: readonly string[]): boolean {
  return Object.keys(value).length === keys.length && Object.keys(value).every((key) => keys.includes(key));
}
function decimal(value: unknown, max: bigint): value is string {
  if (typeof value !== "string" || value.length > 78 || !/^(0|[1-9][0-9]*)$/.test(value)) return false;
  try { return BigInt(value) <= max; } catch { return false; }
}

/** Validate the engine-produced terminal proof before discharging one old register dispatch. */
export function hasVerifiedUnusedRetirement(record: unknown): record is Row {
  if (!isRecord(record) || record.phase !== "retired_unused" || record.activeAction !== null ||
      record.reservationClaimed !== true || record.paymentEvidence != null || !Number.isSafeInteger(record.retiredAt) || Number(record.retiredAt) < 0 ||
      !isRecord(record.deployment) || !isRecord(record.reservation) || record.reservation.kind !== "voucher" ||
      record.reservation.schemaVersion !== 2 || record.reservation.state !== "quoted" ||
      !isRecord(record.reservation.voucher) || !isRecord(record.finalizedEvidence)) return false;
  const deployment = record.deployment as Row;
  const reservation = record.reservation as Row;
  const voucher = reservation.voucher as Row;
  const evidence = record.finalizedEvidence as Row;
  if (!exactKeys(evidence, ["blockNumber", "blockHash", "timestamp", "reservationConsumed"]) ||
      evidence.reservationConsumed !== false || typeof evidence.blockHash !== "string" || !HASH.test(evidence.blockHash) ||
      evidence.blockHash !== evidence.blockHash.toLowerCase() || !decimal(evidence.blockNumber, MAX_SAFE_BLOCK) ||
      !decimal(evidence.timestamp, MAX_UINT64) || !decimal(deployment.deploymentBlockNumber, MAX_SAFE_BLOCK) ||
      BigInt(evidence.blockNumber) < BigInt(deployment.deploymentBlockNumber) ||
      !decimal(voucher.payBefore, MAX_UINT64) || BigInt(evidence.timestamp) <= BigInt(voucher.payBefore) ||
      reservation.reservationId !== voucher.reservationId || typeof record.payer !== "string" || !ADDRESS.test(record.payer) ||
      record.payer !== record.payer.toLowerCase() || typeof deployment.registryAddress !== "string" ||
      !ADDRESS.test(deployment.registryAddress) || deployment.registryAddress !== deployment.registryAddress.toLowerCase()) return false;
  return true;
}

/** True only for the exact historic register transaction covered by a finalized-unused voucher proof. */
export function isVerifiedRetiredUnusedPurchaseAction(record: unknown, action: unknown): boolean {
  if (!hasVerifiedUnusedRetirement(record) || !isRecord(action) || action.kind !== "purchase" || action.enteredDispatch !== true ||
      !["uncertain", "submitted"].includes(String(action.status)) || !isRecord(action.transaction) ||
      !exactKeys(action.transaction, ["from", "to", "data", "value"]) || action.transaction.from !== record.payer ||
      action.transaction.to !== (record.deployment as Row).registryAddress || action.transaction.value !== "0x0" ||
      typeof action.actionId !== "string" || action.actionId.length < 1 || action.actionId.length > 64 ||
      !exactKeys(action, ["kind", "actionId", "status", "enteredDispatch", "transaction", "returnedHash"]) ||
      typeof action.transaction.data !== "string" || !Array.isArray(record.actionHistory) ||
      record.actionHistory.filter((item) => isRecord(item) && item.actionId === action.actionId).length !== 1 ||
      !record.actionHistory.some((item) => isRecord(item) && item.actionId === action.actionId && item.kind === "purchase")) return false;
  if (action.status === "submitted") {
    if (typeof action.returnedHash !== "string" || !HASH.test(action.returnedHash) || action.returnedHash !== action.returnedHash.toLowerCase()) return false;
  } else if (action.returnedHash !== null && !(typeof action.returnedHash === "string" && HASH.test(action.returnedHash) && action.returnedHash === action.returnedHash.toLowerCase())) return false;
  if (action.evidence != null) return false;
  const reservation = record.reservation as Row;
  const voucher = reservation.voucher as Row;
  const signature = reservation.signature;
  if (typeof signature !== "string" || !SIGNATURE.test(signature) || signature !== signature.toLowerCase() ||
      typeof record.name !== "string" || !decimal(voucher.slotEpoch, MAX_UINT64) || !decimal(voucher.quoteSignerEpoch, MAX_UINT64) ||
      !decimal(voucher.quoteNonce, (1n << 256n) - 1n) || !decimal(voucher.issuedAt, MAX_UINT64) ||
      !decimal(voucher.payBefore, MAX_UINT64) || !decimal(voucher.uploadBefore, MAX_UINT64) ||
      !decimal(voucher.fulfillBefore, MAX_UINT64) || !decimal(voucher.price, (1n << 96n) - 1n)) return false;
  try {
    const typedVoucher = { ...voucher, slotEpoch: BigInt(voucher.slotEpoch as string), quoteSignerEpoch: BigInt(voucher.quoteSignerEpoch as string),
      quoteNonce: BigInt(voucher.quoteNonce as string), issuedAt: BigInt(voucher.issuedAt as string), payBefore: BigInt(voucher.payBefore as string),
      uploadBefore: BigInt(voucher.uploadBefore as string), fulfillBefore: BigInt(voucher.fulfillBefore as string), price: BigInt(voucher.price as string) };
    const expectedData = encodeFunctionData({ abi: botnetRegistryV2Abi, functionName: "registerWithVoucher",
      args: [typedVoucher as never, record.name, BigInt(voucher.price as string), signature as `0x${string}`] });
    return action.transaction.data === expectedData;
  } catch { return false; }
}

export function hasVerifiedRetiredUnusedPurchaseHistory(record: unknown): boolean {
  return hasVerifiedUnusedRetirement(record) && Array.isArray(record.actionHistory) &&
    record.actionHistory.some((action) => isVerifiedRetiredUnusedPurchaseAction(record, action));
}

/**
 * A lost appendPromptChunk response is discharged only by the complete canonical
 * progress prefix already persisted for the same paid agent. The action and its
 * purchase binding are checked against the exact calldata the engine constructs.
 */
export function isVerifiedCoveredPromptChunkAction(record: unknown, action: unknown): boolean {
  if (!isRecord(record) || (record.phase !== "paid" && record.phase !== "queued") || record.activeAction !== null ||
      !isRecord(record.deployment) || !isRecord(record.reservation) || record.reservation.kind !== "voucher" ||
      record.reservation.state !== "quoted" || !isRecord(record.reservation.voucher) || !isRecord(record.draft) ||
      typeof record.draft.additionalPrompt !== "string" || typeof record.payer !== "string" || !ADDRESS.test(record.payer) ||
      record.payer !== record.payer.toLowerCase() || typeof record.deployment.registryAddress !== "string" ||
      !ADDRESS.test(record.deployment.registryAddress) || record.deployment.registryAddress !== record.deployment.registryAddress.toLowerCase() ||
      !isRecord(record.paymentEvidence) || !exactKeys(record.paymentEvidence, ["transactionHash", "agentId", "blockNumber", "blockHash"]) ||
      typeof record.paymentEvidence.transactionHash !== "string" || !HASH.test(record.paymentEvidence.transactionHash) ||
      record.paymentEvidence.transactionHash !== record.paymentEvidence.transactionHash.toLowerCase() ||
      !decimal(record.paymentEvidence.agentId, MAX_UINT256) || !decimal(record.paymentEvidence.blockNumber, MAX_SAFE_BLOCK) ||
      !decimal(record.deployment.deploymentBlockNumber, MAX_SAFE_BLOCK) ||
      BigInt(record.paymentEvidence.blockNumber) < BigInt(record.deployment.deploymentBlockNumber) ||
      typeof record.paymentEvidence.blockHash !== "string" || !HASH.test(record.paymentEvidence.blockHash) ||
      record.paymentEvidence.blockHash !== record.paymentEvidence.blockHash.toLowerCase() ||
      !isRecord(record.promptProgress) || !exactKeys(record.promptProgress, ["received", "receivedBytes", "digest"]) ||
      !Number.isSafeInteger(record.promptProgress.received) || !Number.isSafeInteger(record.promptProgress.receivedBytes) ||
      typeof record.promptProgress.digest !== "string" || !HASH.test(record.promptProgress.digest) ||
      record.promptProgress.digest !== record.promptProgress.digest.toLowerCase() || !isRecord(action)) return false;

  const payment = record.paymentEvidence;
  const progress = record.promptProgress;
  const allowedActionKeys = ["kind", "actionId", "status", "enteredDispatch", "transaction", "returnedHash", "chunkIndex", "agentId"];
  if (!exactKeys(action, allowedActionKeys) || action.kind !== "chunk" ||
      (action.status !== "uncertain" && action.status !== "submitted") || action.enteredDispatch !== true ||
      typeof action.actionId !== "string" || action.actionId.length < 1 || action.actionId.length > 64 ||
      !Number.isSafeInteger(action.chunkIndex) || Number(action.chunkIndex) < 0 ||
      !decimal(action.agentId, MAX_UINT256) || action.agentId !== payment.agentId || !isRecord(action.transaction)) return false;
  if (action.status === "uncertain" ? action.returnedHash !== null :
      typeof action.returnedHash !== "string" || !HASH.test(action.returnedHash) || action.returnedHash !== action.returnedHash.toLowerCase()) return false;

  const history = record.actionHistory;
  if (!Array.isArray(history) || history.filter((item) => isRecord(item) && item.actionId === action.actionId).length !== 1) return false;
  const purchaseActions = history.filter((item) => isRecord(item) && item.kind === "purchase" && item.returnedHash === payment.transactionHash);
  if (purchaseActions.length !== 1 || !isRecord(purchaseActions[0]) ||
      history.filter((item) => isRecord(item) && item.actionId === purchaseActions[0]!.actionId).length !== 1 ||
      !isVerifiedPaymentPurchase(record, purchaseActions[0])) return false;

  try {
    const plan = encodeV2Utf8Prompt(record.draft.additionalPrompt);
    if (!voucherMatchesPromptPlan(record, plan)) return false;
    const received = Number(progress.received);
    if (received < 0 || received > plan.chunkCount || Number(action.chunkIndex) >= received ||
        (record.phase === "queued" && received !== plan.chunkCount)) return false;
    const expectedBytes = plan.chunks.slice(0, received).reduce((total, chunk) => total + (chunk.data.length - 2) / 2, 0);
    if (Number(progress.receivedBytes) !== expectedBytes || progress.digest !== nextSequence(plan.chunks, received)) return false;
    const chunk = plan.chunks[Number(action.chunkIndex)];
    if (!chunk) return false;
    const expectedData = encodeFunctionData({ abi: botnetRegistryV2Abi, functionName: "appendPromptChunk",
      args: [BigInt(payment.agentId), Number(action.chunkIndex), chunk.data, chunk.proof] });
    const transaction = action.transaction;
    return exactKeys(transaction, ["from", "to", "data", "value"]) && transaction.from === record.payer &&
      transaction.to === record.deployment.registryAddress && transaction.data === expectedData && transaction.value === "0x0";
  } catch { return false; }
}

function voucherMatchesPromptPlan(record: Row, plan: ReturnType<typeof encodeV2Utf8Prompt>): boolean {
  const reservation = record.reservation as Row;
  const voucher = reservation.voucher as Row;
  const deployment = record.deployment as Row;
  const systemPrompt = (record.draft as Row).systemPrompt;
  if (!isRecord(systemPrompt) || typeof record.name !== "string" ||
      typeof voucher.creator !== "string" || voucher.creator !== record.payer ||
      typeof voucher.payer !== "string" || voucher.payer !== record.payer ||
      voucher.reservationId !== reservation.reservationId || voucher.tierId !== record.tierId ||
      !ADDRESS.test(String(voucher.feeReceiver)) || voucher.feeReceiver === `0x${"0".repeat(40)}` ||
      voucher.promptEncoding !== plan.encoding || voucher.promptChunks !== plan.chunkCount ||
      voucher.encodedPromptBytes !== plan.encodedBytes || voucher.decodedPromptBytes !== plan.decodedBytes ||
      voucher.promptRoot !== plan.root || voucher.encodedPromptDigest !== plan.encodedDigest || voucher.decodedPromptDigest !== plan.decodedDigest ||
      voucher.systemPromptHash !== systemPrompt.systemPromptHash ||
      !HASH.test(String(voucher.runtimePolicyHash)) || !HASH.test(String(voucher.accountPolicyHash)) || !HASH.test(String(voucher.settlementPolicyHash)) ||
      voucher.nameHash !== keccak256(toHex(new TextEncoder().encode(record.name))) ||
      !sameExactJson(reservation.deployment, deployment)) return false;
  try {
    const issuedAt = BigInt(String(voucher.issuedAt));
    const payBefore = BigInt(String(voucher.payBefore));
    const uploadBefore = BigInt(String(voucher.uploadBefore));
    const fulfillBefore = BigInt(String(voucher.fulfillBefore));
    return issuedAt <= payBefore && payBefore <= uploadBefore && uploadBefore <= fulfillBefore;
  } catch { return false; }
}

function sameExactJson(left: unknown, right: unknown): boolean {
  const stable = (value: unknown): string => Array.isArray(value) ? `[${value.map(stable).join(",")}]`
    : isRecord(value) ? `{${Object.keys(value).sort().map((key) => `${JSON.stringify(key)}:${stable(value[key])}`).join(",")}}`
    : JSON.stringify(value) ?? "null";
  try { return stable(left) === stable(right); } catch { return false; }
}

export function hasVerifiedCoveredPromptChunkHistory(record: unknown): boolean {
  return isRecord(record) && Array.isArray(record.actionHistory) && record.actionHistory.some((action) =>
    isRecord(action) && action.status === "uncertain" && isVerifiedCoveredPromptChunkAction(record, action));
}

export function hasUnresolvedTerminalFinancialActivity(record: unknown): boolean {
  if (!isRecord(record) || !Array.isArray(record.actionHistory)) return true;
  const payment = isRecord(record.paymentEvidence) ? record.paymentEvidence : null;
  const approval = isRecord(record.approvalEvidence) ? record.approvalEvidence : null;
  for (const raw of record.actionHistory) {
    if (!isRecord(raw)) return true;
    if (raw.kind === "chunk") continue;
    if (raw.kind !== "approve" && raw.kind !== "purchase") return true;
    if (raw.status === "failed" && raw.enteredDispatch === false && raw.returnedHash === null) continue;
    if (raw.status === "submitted" && raw.kind === "purchase" && payment &&
        raw.returnedHash === payment.transactionHash && isVerifiedPaymentPurchase(record, raw)) continue;
    if (raw.status === "submitted" && raw.kind === "approve" && approval && raw.returnedHash === approval.transactionHash) continue;
    return true;
  }
  if (record.activeAction != null && (!isRecord(record.activeAction) || record.activeAction.kind !== "chunk")) return true;
  if (isRecord(record.budgetFunding)) {
    const funding = record.budgetFunding;
    if (funding.activeAction != null || ["approval_pending", "deposit_pending", "refund_pending", "uncertain"].includes(String(funding.status))) return true;
    if (Array.isArray(funding.actionHistory)) {
      const knownHashes = [funding.approvalEvidence, funding.depositEvidence, funding.returnedEvidence]
        .filter(isRecord).map((item) => item.transactionHash);
      for (const action of funding.actionHistory) {
        if (!isRecord(action) || action.status === "uncertain" || action.status === "dispatching" ||
            action.status === "submitted" && !knownHashes.includes(action.hash)) return true;
      }
    }
  }
  return false;
}

function hasTerminalRefundProof(record: unknown): record is Row {
  if (!isRecord(record) || record.phase !== "terminal_onchain" || record.activeAction !== null ||
      !isRecord(record.terminalEvidence) || !isRecord(record.paymentEvidence) || !isRecord(record.deployment) ||
      !isRecord(record.reservation) || record.reservation.kind !== "voucher" || !isRecord(record.reservation.voucher) ||
      !Number.isSafeInteger(record.retiredAt) || Number(record.retiredAt) < 0 || hasUnresolvedTerminalFinancialActivity(record)) return false;
  const proof = record.terminalEvidence as Row;
  const payment = record.paymentEvidence as Row;
  if (!exactKeys(proof, ["schemaVersion", "kind", "agentId", "finalizedBlockNumber", "finalizedBlockHash", "observedBlockNumber", "observedBlockHash", "transactionHash", "reservationId"]) ||
      proof.schemaVersion !== 1 || proof.kind !== "refunded_purchase" || proof.agentId !== payment.agentId ||
      proof.transactionHash !== payment.transactionHash || proof.reservationId !== (record.reservation as Row).reservationId ||
      !decimal(proof.finalizedBlockNumber, MAX_SAFE_BLOCK) || !decimal(proof.observedBlockNumber, MAX_SAFE_BLOCK) ||
      BigInt(proof.finalizedBlockNumber) > BigInt(proof.observedBlockNumber) ||
      typeof proof.finalizedBlockHash !== "string" || !HASH.test(proof.finalizedBlockHash) || proof.finalizedBlockHash !== proof.finalizedBlockHash.toLowerCase() ||
      typeof proof.observedBlockHash !== "string" || !HASH.test(proof.observedBlockHash) || proof.observedBlockHash !== proof.observedBlockHash.toLowerCase() ||
      typeof proof.transactionHash !== "string" || !HASH.test(proof.transactionHash) ||
      typeof proof.reservationId !== "string" || !HASH.test(proof.reservationId)) return false;
  const purchases = (record.actionHistory as unknown[]).filter((item) => isRecord(item) && item.kind === "purchase" && item.returnedHash === payment.transactionHash);
  return purchases.length === 1 && isVerifiedPaymentPurchase(record, purchases[0]);
}

export function hasVerifiedTerminalOnchainPurchaseHistory(record: unknown): boolean {
  return hasTerminalRefundProof(record);
}

export function hasVerifiedTerminalOnchainHistory(record: unknown): boolean {
  if (!hasTerminalRefundProof(record) || !Array.isArray(record.actionHistory)) return false;
  return record.actionHistory.every((action) => !isRecord(action) || action.kind !== "chunk" ||
    !["dispatching", "uncertain", "submitted"].includes(String(action.status)) || isVerifiedTerminalOnchainAppendAction(record, action));
}

/** A finalized refunded purchase retires only the exact zero-value append authority it binds. */
export function isVerifiedTerminalOnchainAppendAction(record: unknown, action: unknown): boolean {
  if (!hasTerminalRefundProof(record) || !isRecord(record.deployment) || !isRecord(record.reservation) ||
      record.reservation.kind !== "voucher" || !isRecord(record.reservation.voucher) ||
      !isRecord(record.draft) || typeof record.draft.additionalPrompt !== "string" || !Array.isArray(record.actionHistory) ||
      !Number.isSafeInteger(record.retiredAt) || Number(record.retiredAt) < 0) return false;
  const payment = record.paymentEvidence as Row;
  if (!isRecord(action) || action.kind !== "chunk" || action.enteredDispatch !== true ||
      !["uncertain", "submitted"].includes(String(action.status)) ||
      !exactKeys(action, ["kind", "actionId", "status", "enteredDispatch", "transaction", "returnedHash", "chunkIndex", "agentId"]) ||
      typeof action.actionId !== "string" || action.actionId.length < 1 || action.actionId.length > 64 ||
      !Number.isSafeInteger(action.chunkIndex) || Number(action.chunkIndex) < 0 || action.agentId !== payment.agentId || !isRecord(action.transaction)) return false;
  if (action.status === "uncertain" ? action.returnedHash !== null :
      typeof action.returnedHash !== "string" || !HASH.test(action.returnedHash) || action.returnedHash !== action.returnedHash.toLowerCase()) return false;
  const history = record.actionHistory as unknown[];
  if (history.filter((item) => isRecord(item) && item.actionId === action.actionId).length !== 1) return false;
  const purchase = history.filter((item) => isRecord(item) && item.kind === "purchase" && item.returnedHash === payment.transactionHash);
  if (purchase.length !== 1 || !isVerifiedPaymentPurchase(record, purchase[0])) return false;
  try {
    const plan = encodeV2Utf8Prompt(record.draft.additionalPrompt);
    if (!voucherMatchesPromptPlan(record, plan)) return false;
    const chunk = plan.chunks[Number(action.chunkIndex)];
    if (!chunk) return false;
    const expected = encodeFunctionData({ abi: botnetRegistryV2Abi, functionName: "appendPromptChunk",
      args: [BigInt(payment.agentId as string), Number(action.chunkIndex), chunk.data, chunk.proof] });
    return exactKeys(action.transaction, ["from", "to", "data", "value"]) &&
      action.transaction.from === record.payer && action.transaction.to === (record.deployment as Row).registryAddress &&
      action.transaction.data === expected && action.transaction.value === "0x0";
  } catch { return false; }
}

export function hasVerifiedTerminalOnchainAppendHistory(record: unknown): boolean {
  return isRecord(record) && Array.isArray(record.actionHistory) && record.actionHistory.some((action) =>
    isVerifiedTerminalOnchainAppendAction(record, action));
}

function isVerifiedPaymentPurchase(record: Row, action: unknown): boolean {
  if (!isRecord(action) || !exactKeys(action, ["kind", "actionId", "status", "enteredDispatch", "transaction", "returnedHash", "evidence"]) ||
      action.kind !== "purchase" || action.status !== "submitted" || action.enteredDispatch !== true ||
      typeof action.actionId !== "string" || action.actionId.length < 1 || action.actionId.length > 64 ||
      action.returnedHash !== (record.paymentEvidence as Row).transactionHash || !isRecord(action.evidence) ||
      !exactKeys(action.evidence, ["agentId", "blockNumber", "blockHash"]) || !isRecord(action.transaction) ||
      !exactKeys(action.transaction, ["from", "to", "data", "value"])) return false;
  const payment = record.paymentEvidence as Row;
  const evidence = action.evidence as Row;
  if (evidence.agentId !== payment.agentId || evidence.blockNumber !== payment.blockNumber || evidence.blockHash !== payment.blockHash ||
      action.transaction.from !== record.payer || action.transaction.to !== (record.deployment as Row).registryAddress || action.transaction.value !== "0x0") return false;
  const voucher = (record.reservation as Row).voucher as Row;
  const voucherKeys = ["creator", "payer", "reservationId", "capacitySlotId", "slotEpoch", "tierId", "price", "feeReceiver", "nameHash", "promptEncoding",
    "promptChunks", "promptRoot", "encodedPromptDigest", "decodedPromptDigest", "encodedPromptBytes", "decodedPromptBytes", "systemPromptHash", "runtimePolicyHash",
    "accountPolicyHash", "settlementPolicyHash", "quoteSignerEpoch", "quoteNonce", "issuedAt", "payBefore", "uploadBefore", "fulfillBefore"];
  if (!exactKeys(voucher, voucherKeys) || voucher.reservationId !== (record.reservation as Row).reservationId ||
      typeof (record.reservation as Row).signature !== "string" || !SIGNATURE.test((record.reservation as Row).signature as string)) return false;
  const decimals: Array<[string, bigint]> = [["slotEpoch", MAX_UINT64], ["quoteSignerEpoch", MAX_UINT64], ["quoteNonce", MAX_UINT256],
    ["issuedAt", MAX_UINT64], ["payBefore", MAX_UINT64], ["uploadBefore", MAX_UINT64], ["fulfillBefore", MAX_UINT64], ["price", (1n << 96n) - 1n]];
  if (decimals.some(([key, max]) => !decimal(voucher[key], max))) return false;
  try {
    const typedVoucher = { ...voucher, slotEpoch: BigInt(voucher.slotEpoch as string), quoteSignerEpoch: BigInt(voucher.quoteSignerEpoch as string),
      quoteNonce: BigInt(voucher.quoteNonce as string), issuedAt: BigInt(voucher.issuedAt as string), payBefore: BigInt(voucher.payBefore as string),
      uploadBefore: BigInt(voucher.uploadBefore as string), fulfillBefore: BigInt(voucher.fulfillBefore as string), price: BigInt(voucher.price as string) };
    const data = encodeFunctionData({ abi: botnetRegistryV2Abi, functionName: "registerWithVoucher",
      args: [typedVoucher as never, String(record.name), BigInt(voucher.price as string), (record.reservation as Row).signature as `0x${string}`] });
    return action.transaction.data === data;
  } catch { return false; }
}

function nextSequence(chunks: ReturnType<typeof encodeV2Utf8Prompt>["chunks"], received: number): string {
  if (chunks.length === 0) return ZERO32;
  let state = keccak256(encodeAbiParameters([{ type: "bytes32" }, { type: "uint8" }, { type: "uint32" }], [PROMPT_SEQUENCE_TYPEHASH, 0, chunks.length]));
  for (let index = 0; index < received; index++) {
    const data = chunks[index]!.data;
    state = keccak256(encodeAbiParameters([{ type: "bytes32" }, { type: "uint32" }, { type: "uint32" }, { type: "bytes32" }],
      [state, index, (data.length - 2) / 2, keccak256(data)]));
  }
  return state;
}

/** Retired records may receive one late hash update, but cannot originate new wallet actions. */
export function safeRetiredUnusedHistoryUpdate(current: unknown, next: unknown): boolean {
  if (!isRecord(current) || current.phase !== "retired_unused") return true;
  if (!isRecord(next) || next.phase !== "retired_unused" || next.activeAction !== null ||
      !hasVerifiedUnusedRetirement(next) || !Array.isArray(current.actionHistory) || !Array.isArray(next.actionHistory) ||
      current.actionHistory.length !== next.actionHistory.length) return false;
  const expectedNext = { ...current, revision: next.revision, actionHistory: next.actionHistory };
  if (!sameJson(expectedNext, next)) return false;
  const oldHistory = current.actionHistory as unknown[];
  const nextHistory = next.actionHistory as unknown[];
  return oldHistory.every((oldValue, index) => {
    const oldAction = isRecord(oldValue) ? oldValue : null;
    const newAction = isRecord(nextHistory[index]) ? nextHistory[index] as Row : null;
    if (!oldAction || !newAction || oldAction.actionId !== newAction.actionId || oldAction.kind !== newAction.kind ||
        oldAction.enteredDispatch !== newAction.enteredDispatch || !sameJson(oldAction.transaction, newAction.transaction)) return false;
    if (sameJson(oldAction, newAction)) return true;
    if (oldAction.kind === "purchase") {
      return isVerifiedRetiredUnusedPurchaseAction(current, oldAction) && isVerifiedRetiredUnusedPurchaseAction(next, newAction) &&
        oldAction.status === "uncertain" && newAction.status === "submitted" &&
        (oldAction.returnedHash === null || oldAction.returnedHash === newAction.returnedHash);
    }
    return oldAction.kind === "approve" && oldAction.status === "uncertain" && newAction.status === "submitted" &&
      typeof newAction.returnedHash === "string" && HASH.test(newAction.returnedHash) && newAction.returnedHash === newAction.returnedHash.toLowerCase() &&
      (oldAction.returnedHash === null || oldAction.returnedHash === newAction.returnedHash) && onlyStatusAndHashChanged(oldAction, newAction);
  });
}

/** Permit only terminal retirement and a later hash annotation for the already-retired append. */
export function safeTerminalOnchainHistoryUpdate(current: unknown, next: unknown): boolean {
  if (!isRecord(current)) return false;
  if (current.phase !== "terminal_onchain") {
    if (!isRecord(next) || next.phase !== "terminal_onchain") return true;
    if (!(["paid", "queued"].includes(String(current.phase))) ||
        next.activeAction !== null || !hasVerifiedTerminalOnchainHistory(next) || !Array.isArray(current.actionHistory) ||
        !Array.isArray(next.actionHistory) || current.actionHistory.length !== next.actionHistory.length ||
        !Number.isSafeInteger(next.retiredAt) || Number(next.retiredAt) < 0) return false;
    const proof = { ...next }; delete proof.phase; delete proof.revision; delete proof.activeAction; delete proof.actionHistory;
    delete proof.retiredAt; delete proof.terminalEvidence;
    const before = { ...current }; delete before.phase; delete before.revision; delete before.activeAction; delete before.actionHistory;
    const active = isRecord(current.activeAction) ? current.activeAction : null;
    const beforeHistory = current.actionHistory as unknown[];
    const afterHistory = next.actionHistory as unknown[];
    return sameJson(proof, before) && beforeHistory.every((oldValue, index) => {
      const oldAction = isRecord(oldValue) ? oldValue : null;
      const newAction = isRecord(afterHistory[index]) ? afterHistory[index] as Row : null;
      if (!oldAction || !newAction || oldAction.actionId !== newAction.actionId) return false;
      if (sameJson(oldAction, newAction)) return true;
      const normalizedDispatch = oldAction.kind === "chunk" && oldAction.status === "dispatching" && oldAction.returnedHash === null &&
        newAction.status === "uncertain" && newAction.returnedHash === null &&
        sameJson({ ...oldAction, status: "uncertain" }, newAction);
      return (oldAction.kind === "chunk" && oldAction.status === "uncertain" && oldAction.returnedHash === null &&
        isVerifiedTerminalOnchainAppendAction(next, newAction) && sameJson(oldAction, newAction)) || normalizedDispatch;
    }) && (!active || active.kind === "chunk" && active.enteredDispatch === true && ["dispatching", "uncertain", "submitted"].includes(String(active.status)) &&
      current.actionHistory.some((item) => isRecord(item) && item.actionId === active.actionId) &&
      next.actionHistory.some((item) => isRecord(item) && item.actionId === active.actionId && isVerifiedTerminalOnchainAppendAction(next, item)));
  }
  if (!isRecord(next) || next.phase !== "terminal_onchain" || next.activeAction !== null ||
      !hasVerifiedTerminalOnchainHistory(next) || !Array.isArray(current.actionHistory) || !Array.isArray(next.actionHistory) ||
      current.actionHistory.length !== next.actionHistory.length) return false;
  const expectedNext = { ...current, revision: next.revision, actionHistory: next.actionHistory };
  if (!sameJson(expectedNext, next)) return false;
  const beforeHistory = current.actionHistory as unknown[];
  const afterHistory = next.actionHistory as unknown[];
  return beforeHistory.every((oldValue, index) => {
    const oldAction = isRecord(oldValue) ? oldValue : null;
    const newAction = isRecord(afterHistory[index]) ? afterHistory[index] as Row : null;
    if (!oldAction || !newAction || oldAction.actionId !== newAction.actionId || oldAction.kind !== newAction.kind ||
        oldAction.enteredDispatch !== newAction.enteredDispatch || !sameJson(oldAction.transaction, newAction.transaction)) return false;
    if (sameJson(oldAction, newAction)) return true;
    if (!isVerifiedTerminalOnchainAppendAction(current, oldAction) || newAction.kind !== "chunk" || newAction.status !== "submitted" ||
        typeof newAction.returnedHash !== "string" || !HASH.test(newAction.returnedHash) || newAction.returnedHash !== newAction.returnedHash.toLowerCase()) return false;
    const oldHash = oldAction.returnedHash;
    return (oldHash === null || oldHash === newAction.returnedHash) && onlyStatusAndHashChanged(oldAction, newAction);
  });
}

function onlyStatusAndHashChanged(before: Row, after: Row): boolean {
  const left = { ...before }; const right = { ...after };
  delete left.status; delete left.returnedHash;
  delete right.status; delete right.returnedHash;
  return sameJson(left, right);
}

function sameJson(left: unknown, right: unknown): boolean {
  try { return JSON.stringify(left) === JSON.stringify(right); } catch { return false; }
}
